import React, { useState, useEffect, useCallback } from "react";
import QuoteBuilder from "./QuoteBuilder";
import {
  crmFetch, STAGES, ACTIVITY_TYPES, activityInfo,
  formatMoney, formatDate, formatDateTime, dueLabel, toInputDate, LEAD_SOURCES,
  renderTemplate, crmShareUrl,
} from "./crmApi";

const QUOTE_STATUS = {
  sent: { label: "Awaiting reply", color: "#f59e0b", icon: "📨" },
  accepted: { label: "Accepted", color: "#10b981", icon: "✅" },
  declined: { label: "Declined", color: "#dc2626", icon: "🚫" },
};

/**
 * Slide-over with everything about one lead: client contact info, stage
 * switcher, editable enquiry details, the activity log, and follow-up date.
 * Owned by CrmLeads; onChanged tells the board to refresh.
 */
function LeadDrawer({ lead, onClose, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({});
  const [activities, setActivities] = useState([]);
  const [actType, setActType] = useState("call");
  const [actSummary, setActSummary] = useState("");
  const [lostReason, setLostReason] = useState("");
  const [askLostReason, setAskLostReason] = useState(false);
  const [quote, setQuote] = useState(null);
  const [shareOrigin, setShareOrigin] = useState(null);
  const [quoteLoaded, setQuoteLoaded] = useState(false);
  const [showQuoteBuilder, setShowQuoteBuilder] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);

  useEffect(() => {
    setEditing(false);
    setError("");
    setAskLostReason(false);
    setForm({
      event_type: lead.event_type || "",
      event_date: toInputDate(lead.event_date),
      venue: lead.venue || "",
      budget: lead.budget || "",
      quoted_amount: lead.quoted_amount || "",
      source: lead.source || "",
      notes: lead.notes || "",
      next_follow_up: toInputDate(lead.next_follow_up),
    });
  }, [lead]);

  const loadActivities = useCallback(async () => {
    try {
      const d = await crmFetch(`/api/crm/activities?lead_id=${lead.id}`);
      setActivities(d.activities || []);
    } catch { /* drawer still works without the log */ }
  }, [lead.id]);

  useEffect(() => { loadActivities(); }, [loadActivities]);

  const loadQuote = useCallback(async () => {
    try {
      const d = await crmFetch(`/api/crm/leads/${lead.id}/quote`);
      setQuote(d.quote);
      setShareOrigin(d.share_origin || null);
    } catch { /* section falls back to the create button */ }
    setQuoteLoaded(true);
  }, [lead.id]);

  useEffect(() => { loadQuote(); }, [loadQuote]);

  useEffect(() => {
    crmFetch("/api/crm/templates")
      .then((d) => setTemplates(d.templates || []))
      .catch(() => { /* the drawer works fine without templates */ });
  }, []);

  // The studio's own domain when they have a website, ours otherwise.
  const quoteLink = quote ? crmShareUrl(`/quote/${quote.share_token}`, shareOrigin) : null;

  const copyQuoteLink = async () => {
    try {
      await navigator.clipboard.writeText(quoteLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      window.prompt("Copy the quote link:", quoteLink);
    }
  };

  const openWhatsApp = (message) => {
    const phone = (lead.client_phone || "").replace(/[^\d]/g, "");
    const text = encodeURIComponent(message);
    window.open(
      phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`,
      "_blank", "noopener"
    );
  };

  const whatsappQuote = () =>
    openWhatsApp(`Hi ${lead.client_name}! Here's your quote from us: ${quoteLink}`);

  // Templates are only worth having where the work happens: pick one and it
  // opens WhatsApp with this lead's details already filled in.
  const sendTemplate = (tpl) => {
    openWhatsApp(renderTemplate(tpl.body, { lead, quote, quoteLink }));
    setShowTemplates(false);
    crmFetch("/api/crm/activities", {
      method: "POST",
      body: { lead_id: lead.id, type: "whatsapp", summary: `Sent "${tpl.name}" message` },
    }).then(loadActivities).catch(() => {});
  };

  const onQuoteSaved = (savedQuote) => {
    setQuote(savedQuote);
    setShowQuoteBuilder(false);
    loadActivities();
    onChanged();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const saveDetails = async () => {
    setSaving(true);
    setError("");
    try {
      await crmFetch(`/api/crm/leads/${lead.id}`, {
        method: "PUT",
        body: {
          event_type: form.event_type, event_date: form.event_date || null,
          venue: form.venue, budget: form.budget, quoted_amount: form.quoted_amount,
          source: form.source, notes: form.notes,
          next_follow_up: form.next_follow_up || null,
          event_id: lead.event_id,
        },
      });
      setEditing(false);
      onChanged();
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const moveStage = async (stage) => {
    if (stage === lead.stage) return;
    if (stage === "lost") {
      setAskLostReason(true);
      return;
    }
    try {
      await crmFetch(`/api/crm/leads/${lead.id}/stage`, { method: "PATCH", body: { stage } });
      onChanged();
    } catch (e) {
      setError(e.message);
    }
  };

  const confirmLost = async () => {
    try {
      await crmFetch(`/api/crm/leads/${lead.id}/stage`, {
        method: "PATCH",
        body: { stage: "lost", lost_reason: lostReason },
      });
      setAskLostReason(false);
      setLostReason("");
      onChanged();
    } catch (e) {
      setError(e.message);
    }
  };

  const addActivity = async (e) => {
    e.preventDefault();
    if (!actSummary.trim()) return;
    try {
      await crmFetch("/api/crm/activities", {
        method: "POST",
        body: { lead_id: lead.id, type: actType, summary: actSummary },
      });
      setActSummary("");
      loadActivities();
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteLead = async () => {
    if (!window.confirm(`Delete this lead for ${lead.client_name}? The client stays in your client book.`)) return;
    try {
      await crmFetch(`/api/crm/leads/${lead.id}`, { method: "DELETE" });
      onClose();
      onChanged();
    } catch (e) {
      setError(e.message);
    }
  };

  const due = dueLabel(lead.next_follow_up);

  return (
    <>
      <div className="crm-drawer-backdrop" onClick={onClose} />
      <div className="crm-drawer">
        <div className="crm-drawer-head">
          <div>
            <h3>{lead.client_name}</h3>
            <div className="crm-drawer-sub">
              {lead.client_phone && <span>📱 {lead.client_phone} &nbsp;</span>}
              {lead.client_email && <span>✉️ {lead.client_email}</span>}
            </div>
          </div>
          <button className="crm-drawer-close" onClick={onClose}>✕</button>
        </div>

        <div className="crm-drawer-body">
          {error && <div className="crm-form-error">{error}</div>}

          {templates.length > 0 && (
            <div className="crm-drawer-section">
              <button
                className="crm-btn ghost"
                onClick={() => setShowTemplates(!showTemplates)}
              >
                💬 Send a message{showTemplates ? " ▴" : " ▾"}
              </button>
              {showTemplates && (
                <div className="crm-tpl-picker">
                  {templates.map((t) => (
                    <button key={t.id} className="crm-tpl-option" onClick={() => sendTemplate(t)}>
                      <span className="crm-tpl-option-name">{t.name}</span>
                      <span className="crm-tpl-option-preview">
                        {renderTemplate(t.body, { lead, quote, quoteLink }).slice(0, 90)}…
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="crm-drawer-section">
            <h4 className="crm-section-title">Stage</h4>
            <div className="crm-stage-row">
              {STAGES.map((s) => (
                <button
                  key={s.key}
                  className={`crm-stage-chip ${lead.stage === s.key ? "active" : ""}`}
                  style={lead.stage === s.key ? { background: s.color } : {}}
                  onClick={() => moveStage(s.key)}
                >
                  {s.icon} {s.label}
                </button>
              ))}
            </div>
            {askLostReason && (
              <div style={{ marginTop: 10 }}>
                <div className="crm-field full">
                  <label>Why was this lead lost? (optional)</label>
                  <input
                    value={lostReason}
                    onChange={(e) => setLostReason(e.target.value)}
                    placeholder="e.g. went with another studio, budget too high…"
                  />
                </div>
                <div className="crm-modal-actions" style={{ marginTop: 10 }}>
                  <button className="crm-btn ghost" onClick={() => setAskLostReason(false)}>Cancel</button>
                  <button className="crm-btn danger" onClick={confirmLost}>Mark as lost</button>
                </div>
              </div>
            )}
            {lead.stage === "lost" && lead.lost_reason && (
              <div className="crm-kv"><span className="k">Lost because</span><span className="v">{lead.lost_reason}</span></div>
            )}
          </div>

          <div className="crm-drawer-section">
            <h4 className="crm-section-title">
              Enquiry details
              {!editing && (
                <button className="crm-link-btn" onClick={() => setEditing(true)}>Edit</button>
              )}
            </h4>
            {editing ? (
              <>
                <div className="crm-form-grid">
                  <div className="crm-field">
                    <label>Event type</label>
                    <input value={form.event_type} onChange={set("event_type")} placeholder="Wedding, Birthday…" />
                  </div>
                  <div className="crm-field">
                    <label>Event date</label>
                    <input type="date" value={form.event_date} onChange={set("event_date")} />
                  </div>
                  <div className="crm-field">
                    <label>Venue</label>
                    <input value={form.venue} onChange={set("venue")} />
                  </div>
                  <div className="crm-field">
                    <label>Source</label>
                    <select value={form.source} onChange={set("source")}>
                      <option value="">— Select —</option>
                      {LEAD_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="crm-field">
                    <label>Client budget</label>
                    <input type="number" min="0" value={form.budget} onChange={set("budget")} />
                  </div>
                  <div className="crm-field">
                    <label>Quoted amount</label>
                    <input type="number" min="0" value={form.quoted_amount} onChange={set("quoted_amount")} />
                  </div>
                  <div className="crm-field">
                    <label>Next follow-up</label>
                    <input type="date" value={form.next_follow_up} onChange={set("next_follow_up")} />
                  </div>
                  <div className="crm-field full">
                    <label>Notes</label>
                    <textarea value={form.notes} onChange={set("notes")} />
                  </div>
                </div>
                <div className="crm-modal-actions">
                  <button className="crm-btn ghost" onClick={() => setEditing(false)}>Cancel</button>
                  <button className="crm-btn primary" disabled={saving} onClick={saveDetails}>
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="crm-kv"><span className="k">Event</span><span className="v">{lead.event_type || "—"}</span></div>
                <div className="crm-kv"><span className="k">Event date</span><span className="v">{formatDate(lead.event_date) || "—"}</span></div>
                <div className="crm-kv"><span className="k">Venue</span><span className="v">{lead.venue || "—"}</span></div>
                <div className="crm-kv"><span className="k">Budget</span><span className="v">{formatMoney(lead.budget) || "—"}</span></div>
                <div className="crm-kv"><span className="k">Quoted</span><span className="v">{formatMoney(lead.quoted_amount) || "—"}</span></div>
                <div className="crm-kv"><span className="k">Source</span><span className="v">{lead.source || "—"}</span></div>
                <div className="crm-kv">
                  <span className="k">Next follow-up</span>
                  <span className="v">
                    {formatDate(lead.next_follow_up) || "Not set"}
                    {due && <span className={`crm-due ${due.tone}`}> · {due.text}</span>}
                  </span>
                </div>
                {lead.linked_event_name && (
                  <div className="crm-kv"><span className="k">Linked event</span><span className="v">🎉 {lead.linked_event_name}</span></div>
                )}
                {lead.notes && (
                  <div className="crm-kv"><span className="k">Notes</span><span className="v">{lead.notes}</span></div>
                )}
              </>
            )}
          </div>

          <div className="crm-drawer-section">
            <h4 className="crm-section-title">Quote</h4>
            {!quoteLoaded ? null : quote ? (
              <>
                <div className="crm-quote-card">
                  <div className="crm-quote-card-top">
                    <div>
                      <div className="crm-row-title">{quote.title || quote.quote_number}</div>
                      <div className="crm-row-sub">
                        {quote.quote_number} · {quote.items.length} item{quote.items.length === 1 ? "" : "s"}
                        {quote.valid_until ? ` · valid until ${formatDate(quote.valid_until)}` : ""}
                      </div>
                    </div>
                    <div className="crm-quote-card-right">
                      <div className="crm-lead-amount" style={{ fontSize: 15 }}>{formatMoney(quote.total) || "0"}</div>
                      <span
                        className="crm-badge"
                        style={{
                          background: `${(QUOTE_STATUS[quote.status] || QUOTE_STATUS.sent).color}22`,
                          color: (QUOTE_STATUS[quote.status] || QUOTE_STATUS.sent).color,
                        }}
                      >
                        {(QUOTE_STATUS[quote.status] || QUOTE_STATUS.sent).icon}{" "}
                        {(QUOTE_STATUS[quote.status] || QUOTE_STATUS.sent).label}
                      </span>
                    </div>
                  </div>
                  <div className="crm-quote-card-actions">
                    <button className="crm-btn ghost" onClick={copyQuoteLink}>
                      {linkCopied ? "✓ Copied!" : "🔗 Copy link"}
                    </button>
                    <button className="crm-btn ghost" onClick={whatsappQuote}>💬 WhatsApp</button>
                    <a
                      className="crm-btn ghost"
                      href={quoteLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none" }}
                    >
                      👁 Preview
                    </a>
                    <button className="crm-btn ghost" onClick={() => setShowQuoteBuilder(true)}>✏️ Edit</button>
                  </div>
                </div>
              </>
            ) : (
              <button className="crm-btn primary" onClick={() => setShowQuoteBuilder(true)}>
                ＋ Create quote
              </button>
            )}
          </div>

          <div className="crm-drawer-section">
            <h4 className="crm-section-title">Activity log</h4>
            <form className="crm-activity-form" onSubmit={addActivity}>
              <select value={actType} onChange={(e) => setActType(e.target.value)}>
                {ACTIVITY_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>{t.icon} {t.label}</option>
                ))}
              </select>
              <input
                value={actSummary}
                onChange={(e) => setActSummary(e.target.value)}
                placeholder="What happened? e.g. Discussed package, will decide by Friday"
              />
              <button className="crm-btn primary" type="submit">Log</button>
            </form>
            {activities.length === 0 ? (
              <div className="crm-empty" style={{ padding: "18px 8px" }}>
                No activity yet — log your first call or chat above.
              </div>
            ) : (
              <ul className="crm-timeline" style={{ marginTop: 10 }}>
                {activities.map((a) => (
                  <li key={a.id}>
                    <span className="t-icon">{activityInfo(a.type).icon}</span>
                    <div className="t-body">
                      <div className="t-summary">{a.summary}</div>
                      <div className="t-when">{formatDateTime(a.activity_date)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="crm-drawer-section" style={{ textAlign: "right" }}>
            <button className="crm-btn danger" onClick={deleteLead}>Delete lead</button>
          </div>
        </div>
      </div>

      {showQuoteBuilder && (
        <QuoteBuilder
          lead={lead}
          existingQuote={quote}
          onClose={() => setShowQuoteBuilder(false)}
          onSaved={onQuoteSaved}
        />
      )}
    </>
  );
}

export default LeadDrawer;
